import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import useEmblaCarousel from "embla-carousel-react";
import { Children, useCallback, useEffect, useState } from "react";

/**
 * Pure: which page of the carousel an item is on.
 *
 * Embla gathers the items into pages as they fit the width it has and keeps that
 * grouping as one array of item indices per page, so the answer is simply which
 * of those arrays holds this item. Null for an item on no page — an index of -1
 * from a `findIndex` that matched nothing, or a carousel not yet measured.
 */
export const carouselPageOfItem = (pages, itemIndex) => {
  if (itemIndex == null || itemIndex < 0) return null;
  const page = (pages || []).findIndex(items => items.includes(itemIndex));
  return page === -1 ? null : page;
};

const ARROW_CLASS =
  "shrink-0 rounded-full border border-gray-200 p-1 text-gray-500 transition hover:border-gray-400 hover:text-gray-900 disabled:cursor-default disabled:border-gray-100 disabled:text-gray-300 disabled:hover:text-gray-300";

/**
 * A row of thumbnails a page at a time, with the arrows that turn those pages
 * standing outside the row so they never cover a picture. The row can also be
 * dragged with a mouse or a finger, and settles on a page either way.
 *
 * `selectedIndex` is the item the surrounding form has chosen, if any, and the
 * carousel opens on the page holding it. It turns to that page when the choice
 * changes from elsewhere, rather than sliding the chosen thumbnail to the middle
 * of the row: the neighbours it sits among stay put, so the row reads as a set
 * of pages you can go back to rather than a strip that shifts underfoot.
 */
const ThumbnailCarousel = ({
  selectedIndex = null,
  label = "images",
  className = "",
  children,
}) => {
  const [carouselRef, carousel] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    // Pages of as many thumbnails as fit, which is what the arrows turn and what
    // a drag settles on. The thumbnails are of differing widths (each stands in
    // the shape of its own picture), so only Embla can say how many that is.
    slidesToScroll: "auto",
  });
  const [pageState, setPageState] = useState({ first: true, last: true });
  const itemCount = Children.count(children);

  const readPageState = useCallback(
    api =>
      setPageState({ first: !api.canScrollPrev(), last: !api.canScrollNext() }),
    [],
  );

  useEffect(() => {
    if (!carousel) return undefined;
    readPageState(carousel);
    carousel.on("select", readPageState).on("reInit", readPageState);
    return () => {
      carousel.off("select", readPageState).off("reInit", readPageState);
    };
  }, [carousel, readPageState]);

  // Turn to the page the chosen thumbnail is on. Re-measured when the items
  // change, an image arriving or leaving being enough to repaginate the row and
  // move the chosen one to another page.
  useEffect(() => {
    if (!carousel) return;
    const page = carouselPageOfItem(
      carousel.internalEngine().slideRegistry,
      selectedIndex,
    );
    if (page != null && page !== carousel.selectedScrollSnap()) {
      carousel.scrollTo(page);
    }
  }, [carousel, selectedIndex, itemCount]);

  if (itemCount === 0) return null;

  return (
    <div className={`flex min-w-0 items-center gap-2 ${className}`}>
      <button
        type="button"
        className={ARROW_CLASS}
        aria-label={`Previous ${label}`}
        disabled={pageState.first}
        onClick={() => carousel?.scrollPrev()}
      >
        <ChevronLeftIcon className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1 overflow-hidden" ref={carouselRef}>
        {/* The padding is for the ring a selected thumbnail wears, which would
            otherwise be clipped by the edge of the row. */}
        <div className="flex items-center gap-3 px-1 py-2">
          {Children.map(children, child => (
            <div className="shrink-0">{child}</div>
          ))}
        </div>
      </div>
      <button
        type="button"
        className={ARROW_CLASS}
        aria-label={`Next ${label}`}
        disabled={pageState.last}
        onClick={() => carousel?.scrollNext()}
      >
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

export default ThumbnailCarousel;
